import React, { useState, useRef, useEffect } from 'react';

const BeforeAfterSlider = ({ beforeImage, afterImage, title, beforeLabel = "C4D WIREFRAME", afterLabel = "FINAL RENDER" }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    setSliderPosition(Math.min(Math.max(percentage, 0), 100));
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleTouchMove = (e) => {
    if (e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => setIsDragging(false);
    const handleGlobalMouseMove = (e) => {
      if (isDragging) {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        setSliderPosition(Math.min(Math.max(percentage, 0), 100));
      }
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isDragging]);

  return (
    <div className="w-full mb-24">
      {title && (
        <p className="text-xs tracking-widest uppercase text-gray-600 mb-4" style={{ fontFamily: 'Archivo, sans-serif' }}>
          {title}
        </p>
      )}
      
      <div 
        ref={containerRef}
        className="relative w-full select-none cursor-ew-resize"
        style={{ aspectRatio: '16/9' }}
        onTouchMove={handleTouchMove}
        onTouchStart={() => setIsDragging(true)}
        onTouchEnd={() => setIsDragging(false)}
      >
        {/* After Image (Bottom Layer) */}
        <div className="absolute inset-0">
          <img 
            src={afterImage} 
            alt={afterLabel}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Before Image (Top Layer - Clipped) */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
        >
          <img 
            src={beforeImage} 
            alt={beforeLabel}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Slider Handle */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize"
          style={{ 
            left: `${sliderPosition}%`,
            transform: 'translateX(-50%)',
            boxShadow: '0 0 10px rgba(0,0,0,0.5)'
          }}
          onMouseDown={handleMouseDown}
        >
          {/* Handle Circle */}
          <div 
            className="absolute top-1/2 left-1/2 w-12 h-12 bg-white rounded-none flex items-center justify-center cursor-ew-resize"
            style={{ 
              transform: 'translate(-50%, -50%)',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
            }}
          >
            <div className="flex gap-1">
              <div className="w-0.5 h-4 bg-black"></div>
              <div className="w-0.5 h-4 bg-black"></div>
            </div>
          </div>
        </div>

        {/* Labels */}
        <div className="absolute top-4 left-4 bg-black bg-opacity-70 px-3 py-1">
          <p className="text-xs tracking-widest text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {beforeLabel}
          </p>
        </div>
        <div className="absolute top-4 right-4 bg-black bg-opacity-70 px-3 py-1">
          <p className="text-xs tracking-widest text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
            {afterLabel}
          </p>
        </div>
      </div>

      {/* Caption */}
      <div className="flex items-baseline justify-between mt-4 px-2">
        <p className="text-xs tracking-widest text-gray-500 uppercase" style={{ fontFamily: 'Archivo, sans-serif' }}>
          Interactive Comparison
        </p>
        <p className="text-xs text-gray-400" style={{ fontFamily: 'Archivo, sans-serif' }}>
          Drag slider to compare
        </p>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
